import React from 'react';
import ReactSelect5, { Option } from '@/components/common/ReactSelect5';
import Input from '@/components/ui/Form/Input';
import Icon from '@/components/ui/Icon';
import I18n from '@/modules/i18n';
import { Filters, NO_FILTER } from './types';

interface FilterSelectProps {
  allLabel: string;
  label: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
}

const FilterSelect = ({
  allLabel, label, options, value, onChange,
}: FilterSelectProps) => {
  const withAll = [{ label: allLabel, value: NO_FILTER }, ...options];

  return (
    <div className="tw-w-36 tw-bg-white">
      <ReactSelect5
        aria-label={label}
        options={withAll}
        value={withAll.find(option => option.value === value) ?? withAll[0]}
        onChange={option => onChange((option as Option)?.value ?? NO_FILTER)}
      />
    </div>
  );
};

interface FilterBarProps {
  categoryOptions: Option[];
  filters: Filters;
  gradeOptions: Option[];
  /** ui/Form/Input falls back to `name` for its DOM id, which collides when two managers are open. */
  searchId: string;
  /** Clearing filters unmounts the button that was focused, so the caller needs somewhere to send it. */
  searchRef?: React.Ref<HTMLInputElement>;
  showGrade: boolean;
  showState: boolean;
  stateOptions: Option[];
  onFilterChange: (name: keyof Filters, value: string) => void;
}

// Presentational only — M2.4's Add-correlation panel renders this same bar over a server-backed
// candidate list, so every option list arrives pre-shaped and owns no filtering logic.
const FilterBar = ({
  categoryOptions,
  filters,
  gradeOptions,
  searchId,
  searchRef,
  showGrade,
  showState,
  stateOptions,
  onFilterChange,
}: FilterBarProps) => (
  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-p-3">
    <div className="tw-w-36 tw-bg-white">
      <ReactSelect5
        aria-label={I18n.t('category')}
        options={categoryOptions}
        value={categoryOptions.find(option => option.value === filters.category) ?? categoryOptions[0]}
        onChange={option => onFilterChange('category', (option as Option)?.value ?? NO_FILTER)}
      />
    </div>

    {showState && (
      <FilterSelect
        allLabel={I18n.t('all_states')}
        label={I18n.t('state')}
        options={stateOptions}
        value={filters.state}
        onChange={value => onFilterChange('state', value)}
      />
    )}

    {showGrade && (
      <FilterSelect
        allLabel={I18n.t('all_grades')}
        label={I18n.t('grade_label')}
        options={gradeOptions}
        value={filters.grade}
        onChange={value => onFilterChange('grade', value)}
      />
    )}

    <div className="tw-relative tw-flex-1 tw-min-w-[12rem]">
      <Icon
        className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-z-10
                   tw-text-dark-grey tw-pointer-events-none"
        icon="Search"
        size={14}
      />
      <Input
        ref={searchRef}
        aria-label={I18n.t('search_code_or_description')}
        className="tw-h-[38px] tw-pl-9 tw-pr-3 tw-bg-white"
        id={searchId}
        name="correlation-search"
        placeholder={I18n.t('search_code_or_description')}
        value={filters.search}
        variant="inline"
        onChange={event => onFilterChange('search', event.target.value)}
      />
    </div>
  </div>
);

export default FilterBar;
